import { OktaAuth } from '@okta/okta-auth-js';

const issuer = process.env.REACT_APP_OKTA_ISSUER;
const clientId = process.env.REACT_APP_OKTA_CLIENT_ID;
const redirectUri =
  process.env.REACT_APP_OKTA_REDIRECT_URI ||
  `${window.location.origin}/login/callback`;
const postLogoutRedirectUri =
  process.env.REACT_APP_OKTA_POST_LOGOUT_REDIRECT_URI ||
  `${window.location.origin}/login`;

class OktaService {
  constructor() {
    this.oktaAuth = null;
    this._initPromise = null;
  }

  _ensureClient() {
    if (this.oktaAuth) {
      return this.oktaAuth;
    }
    if (!issuer || !clientId) {
      throw new Error(
        'Okta is not configured. Set REACT_APP_OKTA_ISSUER and REACT_APP_OKTA_CLIENT_ID.'
      );
    }
    this.oktaAuth = new OktaAuth({
      issuer,
      clientId,
      redirectUri,
      postLogoutRedirectUri,
      pkce: true,
      scopes: ['openid', 'profile', 'email', 'offline_access'],
      tokenManager: {
        storage: 'localStorage',
        autoRenew: true,
      },
    });
    return this.oktaAuth;
  }

  getLogoutRedirectUri() {
    // Always match the tab's origin so local vs deployed URIs stay correct.
    // Must be listed under Okta app → General → Sign-out redirect URIs (exact match).
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/login`;
    }
    return postLogoutRedirectUri || '/login';
  }

  init() {
    if (this._initPromise) {
      return this._initPromise;
    }
    this._initPromise = (async () => {
      try {
        const client = this._ensureClient();
        await client.start();
        return client.isAuthenticated();
      } catch (err) {
        console.error('[OktaService] init failed:', err);
        return false;
      }
    })();
    return this._initPromise;
  }

  login() {
    const client = this._ensureClient();
    return client.signInWithRedirect();
  }

  async handleLoginRedirect() {
    const client = this._ensureClient();
    await client.handleLoginRedirect();
    return client.isAuthenticated();
  }

  async getAccessToken() {
    await this.init();
    const client = this._ensureClient();
    const fromHelper = client.getAccessToken?.();
    if (fromHelper) {
      return fromHelper;
    }
    const accessToken = await client.tokenManager.get('accessToken');
    if (!accessToken) {
      return null;
    }
    if (typeof accessToken === 'string') {
      return accessToken;
    }
    return accessToken.accessToken || null;
  }

  async renewTokens() {
    const client = this._ensureClient();
    await client.tokenManager.renew('accessToken');
    return this.getAccessToken();
  }

  async isAuthenticated() {
    try {
      await this.init();
      const client = this._ensureClient();
      return client.isAuthenticated();
    } catch {
      return false;
    }
  }

  /**
   * End the Okta SSO session (not just local tokens), then return to /login.
   *
   * Must keep the idToken until the Okta logout URL is built — Okta needs
   * id_token_hint to clear the browser session cookie. After navigating to
   * Okta, do NOT assign window.location again (that cancels the logout).
   */
  async logout() {
    const appLoginUri = this.getLogoutRedirectUri();
    try {
      await this.init();
      const client = this._ensureClient();
      const tokens = client.tokenManager.getTokensSync?.() || {};
      const idToken = tokens.idToken;
      const accessToken = tokens.accessToken;
      const refreshToken = tokens.refreshToken;

      // Best-effort revoke; never block the Okta logout redirect on revoke failure.
      if (refreshToken) {
        try {
          await client.revokeRefreshToken(refreshToken);
        } catch (e) {
          console.warn('[OktaService] refresh token revoke failed:', e);
        }
      }
      if (accessToken) {
        try {
          await client.revokeAccessToken(accessToken);
        } catch (e) {
          console.warn('[OktaService] access token revoke failed:', e);
        }
      }

      const oktaLogoutUrl = idToken
        ? client.getSignOutRedirectUrl({
            idToken,
            postLogoutRedirectUri: appLoginUri,
          })
        : '';

      this.clearLocalTokens();

      if (oktaLogoutUrl) {
        // Full-page navigation to Okta /v1/logout — clears Okta session cookie.
        window.location.assign(oktaLogoutUrl);
        return;
      }

      // No idToken: try session API, then local redirect.
      try {
        await client.closeSession();
      } catch (e) {
        console.warn('[OktaService] closeSession failed:', e);
      }
      window.location.assign(appLoginUri);
    } catch (err) {
      console.warn('[OktaService] logout failed, forcing local logout:', err);
      this.clearLocalTokens();
      window.location.assign(appLoginUri);
    }
  }

  clearLocalTokens() {
    try {
      if (this.oktaAuth) {
        this.oktaAuth.tokenManager.clear();
      }
    } catch {
      /* ignore */
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('client');
    // Force a fresh OktaAuth on next login after logout.
    this.oktaAuth = null;
    this._initPromise = null;
  }
}

const oktaService = new OktaService();
export default oktaService;
